/* eslint-disable no-nested-ternary */
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import noop from 'lodash-es/noop';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraColumn } from '../../../components/AtiraColumn';
import { AtiraIcon } from '../../../components/AtiraIcon';
import { AtiraRow } from '../../../components/AtiraRow';
import { Button, ButtonProps } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { WarningModal } from '../../../components/WarningModal';
import i18n, { AppLangs } from '../../../i18n';
import { AtiraLinkPlan } from '../../../model/plan/Plan';
import { AtiraLinkPlanDefinition } from '../../../model/plan/Plan-definition';
import { AtiraLinkPlanType } from '../../../model/plan/types/AtiraLinkPlanType.enum';
import { LemonSubscriptionStatus } from '../../../model/plan/types/LemonSubscriptionStatus.enum';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { paymentSliceSelectors } from '../../../redux/payment/payment.selector';
import { paymentActions } from '../../../redux/payment/payment.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { userActions } from '../../../redux/user/user.slice';
import { Breakpoints } from '../../../theme/Breakpoints';
import { FontSize } from '../../../theme/FontSize';
import { Rounded } from '../../../theme/Rounded';
import { Shadow } from '../../../theme/Shadow';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { standardDate } from '../../../utils/Date';
import { isUserOnFreeTrial, isUserOnUnpaidPlan } from '../../../utils/Payment';
import { Collapse, Tooltip } from '../../../utils/local-antd';
import {
  getPlanTitle,
  toUSD,
  translatePerks,
} from '../../../utils/pricingUtils';
import { PlansLoading } from './PlansLoading';

enum NoticeModalType {
  CANCEL = 'CANCEL',
  DOWNGRADE = 'DOWNGRADE',
  UPGRADE = 'UPGRADE',
  RESUME = 'RESUME',
  FREE_TRIAL = 'FREE_TRIAL',
}

const Card = styled(Flex)<{ isPopular?: boolean }>`
  border-radius: ${Rounded.lg};
  flex-direction: column;
  background: ${({ theme, isPopular }) =>
    isPopular
      ? `linear-gradient(0deg, ${theme.accent}, ${theme.main})`
      : theme.bgCanvas};
  box-shadow: ${Shadow.MD};
  transition: all 0.3s ease-in-out;
  position: relative;
  height: 100%;
  overflow: hidden;

  &:hover {
    box-shadow:
      rgba(14, 30, 37, 0.12) 0px 2px 4px 0px,
      rgba(14, 30, 37, 0.32) 0px 2px 16px 0px;
  }
`;

const ContentWrapper = styled(Flex)`
  padding: ${Spacing.m};
  flex-direction: column;
  gap: ${Spacing.m};
  height: 100%;

  @media (min-width: ${Breakpoints.TABLET}) {
    padding: ${Spacing.l};
  }
`;

const CardPlanInfoInnerWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CardTitle = styled(Text)<{ isPopular?: boolean }>`
  font-weight: 700;
  color: ${({ theme, isPopular }) =>
    isPopular ? theme.onMain : theme.fgPrimary};
  text-align: center;
`;

const Price = styled(Text)<{ isPopular?: boolean }>`
  font-size: ${FontSize.l};
  color: ${({ theme, isPopular }) =>
    isPopular ? theme.onMain : theme.fgPrimary};
  font-weight: bold;
`;

const CardCallToActionButton = styled(Button)<{ isPopular: boolean }>`
  text-align: center;
  border-radius: ${Rounded.lg};
  color: ${({ theme, isPopular }) => (isPopular ? theme.main : theme.onMain)};
  transition: all 0.3s;
  padding: ${Spacing.s} ${Spacing.m};
  height: 2.5rem;
  min-width: 16rem;
  margin-top: auto;
  background-color: ${({ theme, isPopular }) =>
    isPopular ? theme.onMain : theme.main};
  border: 0;

  &:hover {
    filter: brightness(0.9);
    -webkit-filter: brightness(0.9);
  }
`;

const CardBottomCallToActionButton = styled(CardCallToActionButton)<{
  isPopular: boolean;
}>`
  width: 100%;
`;

const BigScreenCallToActionButton = styled(CardCallToActionButton)`
  display: none;
  width: 100%;

  @media (min-width: ${Breakpoints.TABLET}) {
    display: block !important;
  }
`;

const SmallScreenCallToActionButton = styled(CardCallToActionButton)`
  margin-top: ${Spacing.l};
  width: 100%;
  margin-inline: auto;

  @media (min-width: ${Breakpoints.TABLET}) {
    display: none !important;
  }
`;

const PerksList = styled.ul<{ isPopular: boolean; hideOnMobile?: boolean }>`
  display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'flex')};
  flex-direction: column;
  gap: ${Spacing.l};
  list-style-type: disc;
  padding-bottom: ${({ hideOnMobile }) => (hideOnMobile ? Spacing.xl : 0)};
  margin-inline-start: ${Spacing.m};

  &,
  & li p {
    color: ${({ theme, isPopular }) =>
      isPopular ? theme.onMain : theme.fgPrimary};
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    display: flex;
  }
`;

const CurrentMark = styled(Text)<{ isPopular: boolean }>`
  position: absolute;
  top: 0;
  right: ${i18n.language === AppLangs.AR ? 'auto' : '0'};
  left: ${i18n.language === AppLangs.AR ? '0' : 'auto'};
  background: ${({ theme, isPopular }) =>
    isPopular ? theme.onMain : theme.main};
  color: ${({ theme, isPopular }) => (isPopular ? theme.main : theme.onMain)};
  padding: ${Spacing.s} ${Spacing.m};
  border-end-start-radius: ${Rounded.lg};
  font-weight: bold;
  font-size: ${FontSize.xs};
  display: flex;
  align-items: center;
  gap: ${Spacing.s};

  span {
    display: none;
  }

  @media (min-width: ${Breakpoints.MOBILE}) {
    span {
      display: inline-block;
    }
  }
`;

const SubscriptionNote = styled(Text)<{
  isPopular?: boolean;
}>`
  border-radius: ${Rounded.md};
  color: ${({ theme, isPopular }) =>
    isPopular ? theme.onMain : theme.lightMain};
  padding: ${Spacing.s} ${Spacing.m};
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
`;

const StyledRow = styled(AtiraRow)`
  justify-content: center;

  .ant-col {
    padding-bottom: 20px;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    .ant-col:first-child {
      padding-inline-end: 5px;
    }

    .ant-col:nth-child(2) {
      padding-inline-start: 5px;
    }
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    .ant-col:nth-child(2) {
      padding-inline-start: 0;
      padding-inline: 10px;
    }

    .ant-col:last-child {
      padding-inline-start: 5px;
    }
  }
`;

const StyledCollapse = styled(Collapse)<{ isPopular: boolean }>`
  border: 0;
  border-radius: 0;
  background-color: transparent;

  .ant-collapse-header {
    border-top: ${({ theme }) => `1px solid ${theme.bdSubtle}`};
    align-items: center !important;
    flex-direction: row-reverse;
  }

  .ant-collapse-header svg {
    width: 25px;
    height: 25px;
  }

  .ant-collapse-expand-icon {
    padding-inline-end: 0 !important;
  }

  .ant-collapse-header-text {
    font-size: ${FontSize.m};
  }

  .ant-collapse-content {
    border-top: 0;
    background-color: transparent;
  }

  .ant-collapse-header * {
    color: ${({ theme, isPopular }) =>
      isPopular ? theme.sub : theme.fgPrimary};
  }

  .ant-collapse-content-box {
    padding: ${Spacing.m} !important;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    display: none;
  }
`;

const NoteContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const PrivatePricingPlans: React.FC = () => {
  const [planId, setPlanId] = useState<string | undefined>(undefined);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [mode, setMode] = useState<NoticeModalType | null>(null);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const plans = useAppSelector(paymentSliceSelectors.selectPlans);
  const loading = useAppSelector(paymentSliceSelectors.selectPlansLoading);
  const checkoutLoading = useAppSelector(
    paymentSliceSelectors.selectCurrentCheckoutURLLoading,
  );
  const userLinks = useAppSelector(
    atiraLinkSliceSelectors.selectUserAtiraLinks,
  );

  const canShowFreeTrialButtons = useCallback(
    (plan: AtiraLinkPlanDefinition) =>
      plan.type !== AtiraLinkPlanType.PLAN_0 &&
      user?.plan?.type === AtiraLinkPlanType.PLAN_0 &&
      user?.plan?.freeTrial.hasUsed !== true &&
      user?.plan?.unpaidPlan?.hasUsed !== true,
    [
      user?.plan?.freeTrial.hasUsed,
      user?.plan?.type,
      user?.plan?.unpaidPlan?.hasUsed,
    ],
  );

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const orderedPerksKeys: (
    | keyof AtiraLinkPlan['perks']
    | 'uniqueQr'
    | 'support'
  )[] = [
    'linkCount',
    'themeCount',
    'design',
    'embed',
    'index',
    'stats',
    'uniqueQr',
    'qrCount',
    'urlsCount',
    'mobileApp',
    'brand',
    'support',
  ];

  const createCheckout = useCallback(
    async (planId: string) => {
      try {
        setPlanId(planId);
        const checkoutURL = await dispatch(
          paymentActions.createCheckout({
            productId: planId,
            userId,
            embed: true,
          }),
        ).unwrap();
        window.createLemonSqueezy();
        window.LemonSqueezy.Url.Open(checkoutURL);
        paymentActions.resetCheckoutURL();
        setPlanId(undefined);
      } catch (e: any) {
        AtiraToast.apiError(e);
        console.log(e);
      }
    },
    [dispatch, userId],
  );

  const onDowngradePlan = async () => {
    try {
      setButtonsDisabled(true);
      await dispatch(
        paymentActions.changePlan({ lemonVariantId: planId!, userId }),
      ).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      AtiraToast.success(t('subscription.plan.noticeModal.downgrade.toast'));
      setTimeout(() => {
        dispatch(userActions.getLoggedInUser({ userId }));
        dispatch(
          AtiraLinkActions.setSelectedAtiraLink(
            userLinks.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime(),
            )[0],
          ),
        );
      }, 6000);
      setMode(null);
      setPlanId(undefined);
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setButtonsDisabled(false);
    }
  };

  const onUpgradePlan = async () => {
    try {
      setButtonsDisabled(true);
      await dispatch(
        paymentActions.changePlan({ lemonVariantId: planId!, userId }),
      ).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      AtiraToast.success(t('subscription.plan.noticeModal.upgrade.toast'), {
        duration: 7,
      });
      setTimeout(() => {
        dispatch(userActions.getLoggedInUser({ userId }));
      }, 6000);
      setMode(null);
      setPlanId(undefined);
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setButtonsDisabled(false);
    }
  };

  const onCancelPlan = async () => {
    try {
      setButtonsDisabled(true);
      setPlanId(user?.plan?.lemonVariantId!);
      await dispatch(paymentActions.cancelPlan()).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      setMode(null);
      AtiraToast.success(t('subscription.plan.noticeModal.cancel.toast'));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setButtonsDisabled(false);
      setPlanId(undefined);
    }
  };

  const onResumePlan = async () => {
    try {
      setButtonsDisabled(true);
      setPlanId(user?.plan?.lemonVariantId!);
      await dispatch(paymentActions.resumeCancelledPlan()).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      setMode(null);
      AtiraToast.success(t('subscription.plan.noticeModal.resume.toast'));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setButtonsDisabled(false);
      setPlanId(undefined);
    }
  };

  const onStartFreeTrial = useCallback(
    async (lemonVarientId?: string) => {
      try {
        if (!lemonVarientId && !planId) {
          console.log(
            'No planId or lemonVarientId provided to start a free trial',
          );
          return;
        }

        setButtonsDisabled(true);
        await dispatch(
          paymentActions.startFreeTrial({
            lemonVariantId: (lemonVarientId as string) || (planId as string),
            userId,
          }),
        ).unwrap();
        await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
        setMode(null);
        AtiraToast.success(
          t('subscription.plan.noticeModal.freeTrial.toast', {
            plan: t(
              getPlanTitle(
                plans?.find(({ lemonVariantId }) => lemonVariantId === planId)
                  ?.type!,
              ),
            ),
          }),
        );
      } catch (e: any) {
        AtiraToast.apiError(e);
        console.log(e);
      } finally {
        setButtonsDisabled(false);
        setPlanId(undefined);
      }
    },
    [dispatch, planId, plans, t, userId],
  );

  const getActionButtonProps = (
    plan: AtiraLinkPlanDefinition,
  ): Partial<ButtonProps> => {
    const userPlan = user?.plan;

    if (!userPlan) {
      return {};
    }

    const plans = Object.keys(AtiraLinkPlanType);
    const isInFreeTrial = isUserOnFreeTrial(user);
    const isOnUnpaidPlan = isUserOnUnpaidPlan(user);
    const canUseFreeTrial =
      userPlan?.freeTrial?.hasUsed !== true &&
      plan.type === AtiraLinkPlanType.PLAN_0 &&
      user.plan?.unpaidPlan?.hasUsed !== true;
    const isCancelled =
      userPlan?.lemonPlanData?.status === LemonSubscriptionStatus.CANCELLED;
    const userCurrentPlanIndex = plans.indexOf(userPlan?.type);
    const targetPlanIndex = plans.indexOf(plan.type);

    const isCurrentPlan = userCurrentPlanIndex === targetPlanIndex;
    const isDowngrade = userCurrentPlanIndex > targetPlanIndex;
    const isUpgrade = userCurrentPlanIndex < targetPlanIndex;

    const getCurrentPlanButtonProps = (): Partial<ButtonProps> => {
      const userPlan = user?.plan;
      if (isInFreeTrial || isOnUnpaidPlan) {
        return {
          title: t('subscription.plan.button.subscribe'),
          onClick: () => createCheckout(plan.lemonVariantId),
        };
      }

      if (isCancelled) {
        return {
          title: t('subscription.plan.button.resume'),
          onClick: () => setMode(NoticeModalType.RESUME),
        };
      }

      if (userPlan?.type !== AtiraLinkPlanType.PLAN_0) {
        return {
          title: t('subscription.plan.button.cancel'),
          onClick: () => setMode(NoticeModalType.CANCEL),
        };
      }

      if (canUseFreeTrial) {
        return {
          title: t('subscription.plan.button.freeTrial'),
          onClick: () => {
            setMode(NoticeModalType.FREE_TRIAL);
            setPlanId(plan.lemonVariantId);
          },
        };
      }

      return {
        title: t('subscription.plan.button.current'),
      };
    };

    const getDowngradeButtonProps = (): Partial<ButtonProps> => {
      if (
        isInFreeTrial ||
        (isOnUnpaidPlan && plan.type !== AtiraLinkPlanType.PLAN_0)
      ) {
        return {
          title: t('subscription.plan.button.subscribe'),
          onClick: () => createCheckout(plan.lemonVariantId),
        };
      }

      if (isCancelled) {
        return {
          title: t('subscription.plan.button.downgrade'),
          onClick: () => {},
          disabled: true,
        };
      }

      return {
        title: t('subscription.plan.button.downgrade'),
        onClick: () => {
          setPlanId(plan.lemonVariantId);
          setMode(NoticeModalType.DOWNGRADE);
        },
        disabled: plan.type === AtiraLinkPlanType.PLAN_0,
      };
    };

    const getUpgradeButtonProps = (): Partial<ButtonProps> => {
      if (isOnUnpaidPlan) {
        return {
          title: t('subscription.plan.button.subscribe'),
          onClick: () => createCheckout(plan.lemonVariantId),
        };
      }

      if (canUseFreeTrial) {
        return {
          title: t('subscription.plan.button.freeTrial'),
          onClick: () => {
            setPlanId(plan.lemonVariantId);
            onStartFreeTrial(plan.lemonVariantId);
          },
        };
      }

      if (isInFreeTrial || user?.plan?.type === AtiraLinkPlanType.PLAN_0) {
        return {
          title: t('subscription.plan.button.subscribe'),
          onClick: () => createCheckout(plan.lemonVariantId),
        };
      }

      if (isCancelled) {
        return {
          title: t('subscription.plan.button.upgrade'),
          onClick: () => {},
          disabled: true,
        };
      }

      return {
        title: t('subscription.plan.button.upgrade'),
        onClick: () => {
          setPlanId(plan.lemonVariantId);
          setMode(NoticeModalType.UPGRADE);
        },
      };
    };

    switch (true) {
      case isCurrentPlan:
        return getCurrentPlanButtonProps();
      case isDowngrade:
        return getDowngradeButtonProps();
      case isUpgrade:
        return getUpgradeButtonProps();
      default:
        return {};
    }
  };

  const getBottomActionButtonProps = (
    plan: AtiraLinkPlanDefinition,
  ): Partial<ButtonProps> => {
    return {
      title: t('subscription.plan.button.freeTrial'),
      onClick: () => {
        setMode(NoticeModalType.FREE_TRIAL);
        setPlanId(plan.lemonVariantId);
      },
    };
  };

  const noticeModalData: Record<
    NoticeModalType,
    {
      title: string;
      description: string;
      confirmButtonText: string;
      closeButtonText: string;
      onClick: VoidFunction;
    }
  > = {
    [NoticeModalType.CANCEL]: {
      title: t('subscription.plan.noticeModal.cancel.title', {
        plan: user?.plan?.type ? t(getPlanTitle(user?.plan?.type)) : '',
      }),
      description: t('subscription.plan.noticeModal.cancel.description', {
        date: standardDate(user?.plan?.lemonPlanData?.renewsAt!),
      }),
      confirmButtonText: t('subscription.plan.noticeModal.cancel.button.ok'),
      closeButtonText: t('subscription.plan.noticeModal.cancel.button.no'),
      onClick: onCancelPlan,
    },
    [NoticeModalType.RESUME]: {
      title: t('subscription.plan.noticeModal.resume.title', {
        plan: user?.plan?.type ? t(getPlanTitle(user?.plan?.type)) : '',
      }),
      description: t('subscription.plan.noticeModal.resume.description', {
        date: standardDate(user?.plan?.lemonPlanData?.renewsAt!),
      }),
      confirmButtonText: t('common.resume'),
      closeButtonText: t('common.no'),
      onClick: onResumePlan,
    },
    [NoticeModalType.DOWNGRADE]: {
      title: t('subscription.plan.noticeModal.downgrade.title', {
        plan: t(
          getPlanTitle(
            plans?.find(({ lemonVariantId }) => lemonVariantId === planId)!
              ?.type,
          )!,
        ),
      }),
      description: t('subscription.plan.noticeModal.downgrade.description', {
        date: standardDate(user?.plan?.lemonPlanData?.renewsAt!),
        price: toUSD(
          plans?.find(({ lemonVariantId }) => lemonVariantId === planId)
            ?.lemonData?.price || 0,
        ),
      }),
      confirmButtonText: t('subscription.plan.noticeModal.downgrade.button.ok'),
      closeButtonText: t('subscription.plan.noticeModal.downgrade.button.no'),
      onClick: onDowngradePlan,
    },
    [NoticeModalType.UPGRADE]: {
      title: t('subscription.plan.noticeModal.upgrade.title', {
        plan: t(
          getPlanTitle(
            plans?.find(({ lemonVariantId }) => lemonVariantId === planId)
              ?.type!,
          )!,
        ),
      }),
      description: t('subscription.plan.noticeModal.upgrade.description', {
        date: standardDate(user?.plan?.lemonPlanData?.renewsAt!),
      }),
      confirmButtonText: t('common.upgrade'),
      closeButtonText: t('common.no'),
      onClick: onUpgradePlan,
    },
    [NoticeModalType.FREE_TRIAL]: {
      title: t('subscription.plan.noticeModal.freeTrial.title', {
        plan: planId
          ? t(
              getPlanTitle(
                plans?.find(({ lemonVariantId }) => lemonVariantId === planId)
                  ?.type!,
              ),
            )
          : '',
      }),
      description: t('subscription.plan.noticeModal.freeTrial.description', {
        plan: planId
          ? t(
              getPlanTitle(
                plans?.find(({ lemonVariantId }) => lemonVariantId === planId)
                  ?.type!,
              ),
            )
          : '',
      }),
      confirmButtonText: t('subscription.plan.noticeModal.freeTrial.button.ok'),
      closeButtonText: t('subscription.plan.noticeModal.freeTrial.button.no'),
      onClick: onStartFreeTrial,
    },
  };

  useEffect(() => {
    dispatch(paymentActions.getAllPlans());
  }, [dispatch]);

  useEffect(() => {
    if (localStorage.getItem('processCheckout') === 'true' && user) {
      const plan = plans?.find(
        ({ _id }) => _id === localStorage.getItem('plan'),
      );
      const mode = localStorage.getItem('mode') as 'payment' | 'free-trial';

      localStorage.setItem('processCheckout', 'false');

      if (plan && plan.type !== AtiraLinkPlanType.PLAN_0) {
        if (mode === 'free-trial' && canShowFreeTrialButtons(plan)) {
          onStartFreeTrial(plan.lemonVariantId);
        } else if (mode === 'payment') {
          createCheckout(plan.lemonVariantId);
        }
      }

      // This is a temp flag sat by signupFlow to avoid multiple API calls;
      // We remove it here
      setTimeout(() => {
        localStorage.removeItem('processCheckout');
        localStorage.removeItem('plan');
        localStorage.removeItem('mode');
      }, 3000);
    }
  }, [canShowFreeTrialButtons, createCheckout, onStartFreeTrial, plans, user]);

  if (loading && !plans) {
    return <PlansLoading />;
  }

  return (
    <Fragment>
      <StyledRow>
        {plans?.map?.((plan) => {
          const isOnFreeTrial =
            plan.type === user?.plan?.freeTrial?.freeTrialPlan &&
            isUserOnFreeTrial(user);
          const isOnUnpaidPlan =
            plan.type === user?.plan?.unpaidPlan?.unpaidPlanType &&
            isUserOnUnpaidPlan(user);

          const isPopular = plan.type === AtiraLinkPlanType.PLAN_1;

          const reorderedPerks = Object.fromEntries(
            orderedPerksKeys.map((key) => [
              key,
              plan.perks[key as keyof AtiraLinkPlan['perks']],
            ]),
          );

          return (
            <AtiraColumn key={plan?._id} xs={24} sm={24} md={12} lg={12} xl={8}>
              <Card isPopular={isPopular}>
                <ContentWrapper>
                  {!user?.plan?.freeTrial?.endsAt &&
                  plan.type === user?.plan?.type ? (
                    <CurrentMark isPopular={isPopular}>
                      <span>{t('subscription.plan.current')}</span>

                      <AtiraIcon icon={faCheck} />
                    </CurrentMark>
                  ) : null}

                  <CardPlanInfoInnerWrapper>
                    <CardTitle isPopular={isPopular}>
                      {t(getPlanTitle(plan.type))}
                    </CardTitle>

                    <Price isPopular={isPopular}>
                      {toUSD(plan.lemonData.price || 0)}
                      <span style={{ fontSize: '0.8rem' }}>
                        {` / ${t('common.per_year')}`}
                      </span>
                    </Price>
                    {plan.type !== AtiraLinkPlanType.PLAN_0 ? (
                      <BigScreenCallToActionButton
                        isPopular={isPopular}
                        loading={
                          planId === plan.lemonVariantId && checkoutLoading
                        }
                        disabled={buttonsDisabled}
                        {...getActionButtonProps(plan)}
                      />
                    ) : null}
                  </CardPlanInfoInnerWrapper>

                  {isOnFreeTrial || isOnUnpaidPlan ? (
                    <NoteContainer>
                      <SubscriptionNote isPopular={isPopular}>
                        <Tooltip
                          title={t(
                            isOnFreeTrial
                              ? 'subscription.plan.noticeModal.freeTrial.cardRibbon.description'
                              : 'subscription.plan.noticeModal.unpaidPlan.cardRibbon.description',
                            {
                              date: standardDate(
                                isOnFreeTrial
                                  ? user?.plan?.freeTrial.endsAt!
                                  : user?.plan?.unpaidPlan?.endsAt!,
                              ),
                            },
                          )}
                        >
                          <span>
                            <AtiraIcon
                              icon={faInfoCircle}
                              color={isPopular ? 'sub' : 'lightMain'}
                            />
                          </span>
                        </Tooltip>

                        {t(
                          isOnFreeTrial
                            ? 'subscription.plan.noticeModal.freeTrial.cardRibbon'
                            : 'subscription.plan.noticeModal.unpaidPlan.cardRibbon',
                        )}
                      </SubscriptionNote>
                    </NoteContainer>
                  ) : null}

                  <PerksList isPopular={isPopular} hideOnMobile>
                    {Object.entries(reorderedPerks)
                      ?.filter(
                        ([key, value]) =>
                          translatePerks(key as any, value) !== null,
                      )
                      ?.map(([key, value], i) => (
                        <li key={key}>
                          <Flex gap="s" alignItems="center">
                            <Text>
                              {/* @ts-ignore */}
                              {t(translatePerks(key, value || plan.type))}
                            </Text>
                            {key === 'index' ? (
                              <Tooltip
                                title={
                                  <span>
                                    {t('plan.index.info')}
                                    <a
                                      href="https://index.atiralink.com"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      https://index.atiralink.com
                                    </a>
                                  </span>
                                }
                              >
                                <Button
                                  icon={faInfoCircle}
                                  iconColor={isPopular ? 'sub' : 'main'}
                                  backgroundColor="transparent"
                                  padding="0"
                                />
                              </Tooltip>
                            ) : null}
                          </Flex>
                        </li>
                      ))}
                  </PerksList>

                  {canShowFreeTrialButtons(plan) ? (
                    <CardBottomCallToActionButton
                      isPopular={isPopular}
                      loading={
                        planId === plan.lemonVariantId && checkoutLoading
                      }
                      disabled={buttonsDisabled}
                      {...getBottomActionButtonProps(plan)}
                    />
                  ) : null}
                </ContentWrapper>

                <StyledCollapse isPopular={isPopular}>
                  <Collapse.Panel header={t('common.features')} key={plan.type}>
                    <PerksList isPopular={isPopular}>
                      {Object.entries(reorderedPerks)
                        ?.filter(
                          ([key, value]) =>
                            translatePerks(key as any, value) !== null,
                        )
                        ?.map(([key, value], i) => (
                          <li key={key}>
                            <Flex gap="s" alignItems="center">
                              <Text>
                                {/* @ts-ignore */}
                                {t(translatePerks(key, value || plan.type))}
                              </Text>
                              {key === 'index' ? (
                                <Tooltip
                                  title={
                                    <span>
                                      {t('plan.index.info')}
                                      <a
                                        href="https://index.atiralink.com"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        https://index.atiralink.com
                                      </a>
                                    </span>
                                  }
                                >
                                  <Button
                                    icon={faInfoCircle}
                                    iconColor={isPopular ? 'sub' : 'main'}
                                    backgroundColor="transparent"
                                    padding="0"
                                  />
                                </Tooltip>
                              ) : null}
                            </Flex>
                          </li>
                        ))}
                    </PerksList>

                    {/* Same as top button but used on small screens */}
                    {plan.type !== AtiraLinkPlanType.PLAN_0 ? (
                      <SmallScreenCallToActionButton
                        isPopular={isPopular}
                        loading={
                          planId === plan.lemonVariantId && checkoutLoading
                        }
                        disabled={buttonsDisabled}
                        {...getActionButtonProps(plan)}
                      />
                    ) : null}
                  </Collapse.Panel>
                </StyledCollapse>
              </Card>
            </AtiraColumn>
          );
        })}
      </StyledRow>

      <WarningModal
        open={Boolean(mode)}
        title={mode ? noticeModalData[mode]?.title : ''}
        description={mode ? noticeModalData[mode]?.description : ''}
        confirmButtonText={mode ? noticeModalData[mode]?.confirmButtonText : ''}
        closeButtonText={mode ? noticeModalData[mode]?.closeButtonText : ''}
        onClose={() => setMode(null)}
        onConfirm={mode ? noticeModalData[mode]?.onClick : noop}
        loading={Boolean(planId) && Boolean(mode) && buttonsDisabled}
      />
    </Fragment>
  );
};
